/**
 * /field/downhole — the downhole field hand's shell (fe: rollout field access).
 *
 * WHY THIS EXISTS. A field_worker login lands in the casing field tree
 * (App.tsx, `isFieldWorker`), which had no downhole routes, so a hand could not
 * log a run, tear a string down or work an inspection although the API lets
 * every one of those through for field_worker (DOWNHOLE_BUILD_ROLES).
 *
 * THE SHAPE IS THE /my-work SHELL'S (trucking and rentals): a sticky header,
 * the page, and a bottom tab bar — Assemblies, My work, and Home back to the
 * /field tiles, which keep clock-in, safety, pay and the rest. Phone first: one
 * column, full-width cards, every action a full-width button.
 *
 * NO OFFICE FUNCTIONS. Nothing here prices, invoices, edits the register, builds
 * or deletes a string, archives a template or reassigns work. A hand logs and
 * closes runs, tears down a returned string, and works the orders assigned to
 * them — the writes the API opens to field_worker that a field hand does.
 *
 * `.field-shell` sets --bottom-chrome to the safe area only, because the casing
 * field tree has no bar. This shell does have one, so it puts the bar's height
 * back for everything under it — the pages and the downhole dialogs alike.
 */
import React from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Home, Layers } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { BOTTOM_NAV_Z } from '../../../lib/layers';

const TABS = [
  { to: '/field/downhole', label: 'Assemblies', icon: Layers, match: (p: string) => p === '/field/downhole' || p.startsWith('/field/downhole/assemblies') },
  { to: '/field/downhole/work', label: 'My work', icon: ClipboardCheck, match: (p: string) => p.startsWith('/field/downhole/work') },
  // Home leaves the shell, so it is never the current tab.
  { to: '/field', label: 'Home', icon: Home, match: () => false },
];

export default function DownholeFieldLayout() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { company } = useAuth();
  return (
    <div
      data-testid="downhole-field-shell"
      className="flex min-h-screen flex-col bg-fieldiq-dark"
      style={{ '--bottom-chrome': 'calc(env(safe-area-inset-bottom, 0px) + 64px)' } as React.CSSProperties}
    >
      <header className="sticky top-0 z-30 border-b border-fieldiq-border bg-fieldiq-card">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate('/field')}
            aria-label="Field home"
            className="grid size-10 shrink-0 place-items-center rounded-lg border border-fieldiq-border text-fieldiq-text"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </button>
          <div className="min-w-0 leading-tight">
            <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Downhole tools</p>
            <p className="truncate text-sm font-bold text-white">{company?.name || 'FieldIQ'}</p>
          </div>
        </div>
      </header>

      <main className="flex-1 pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-3xl px-4 py-4">
          <Outlet />
        </div>
      </main>

      <nav
        aria-label="Downhole"
        className="fixed inset-x-0 bottom-0 border-t border-fieldiq-border bg-fieldiq-card pb-[env(safe-area-inset-bottom,0px)]"
        style={{ zIndex: BOTTOM_NAV_Z }}
      >
        <div className="mx-auto flex max-w-3xl justify-around">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                aria-current={t.match(pathname) ? 'page' : undefined}
                className={`flex h-16 flex-1 flex-col items-center justify-center gap-1 border-t-2 text-xs ${
                  t.match(pathname) ? 'border-fi-amber font-bold text-fieldiq-text' : 'border-transparent text-fieldiq-muted'
                }`}
              >
                <Icon className="size-5" aria-hidden="true" />
                {t.label}
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
